/**
 * Spend Detail Table
 * Detailed breakdown table with sorting
 */

import { useState } from 'react';
import {
  ArrowUp,
  ArrowDown,
  Minus,
  ChevronUp,
  ChevronDown,
  Cloud,
  Phone,
  Server,
  Brain,
  AudioLines,
} from 'lucide-react';
import type { ServiceSpend, ServiceSpendWithProvider, Provider } from '../types';

interface SpendDetailTableProps {
  data: ServiceSpend[] | ServiceSpendWithProvider[];
  currency: string;
  showProvider?: boolean;
}

type SortKey = 'provider' | 'serviceName' | 'currentMonth' | 'previousMonth' | 'percentChange';
type SortDirection = 'asc' | 'desc';

const providerLabels: Record<Provider, string> = {
  gcp: 'GCP',
  twilio: 'Twilio',
  aws: 'AWS',
  openai: 'OpenAI',
  deepgram: 'Deepgram',
};

const providerIcons: Record<Provider, typeof Cloud> = {
  gcp: Cloud,
  twilio: Phone,
  aws: Server,
  openai: Brain,
  deepgram: AudioLines,
};

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function SpendDetailTable({
  data,
  currency,
  showProvider = false,
}: SpendDetailTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('currentMonth');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = (a as ServiceSpendWithProvider)[sortKey as keyof ServiceSpendWithProvider];
    const bValue = (b as ServiceSpendWithProvider)[sortKey as keyof ServiceSpendWithProvider];
    const modifier = sortDirection === 'asc' ? 1 : -1;

    if (aValue === undefined || bValue === undefined) return 0;
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue) * modifier;
    }
    return ((aValue as number) - (bValue as number)) * modifier;
  });

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 inline ml-1" />
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Service Breakdown</h3>
        <p className="text-sm text-gray-500 mt-1">
          {showProvider
            ? 'All services across providers with current and previous month spend'
            : 'All services with current and previous month spend'}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {showProvider && (
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('provider')}
                >
                  Provider <SortIcon columnKey="provider" />
                </th>
              )}
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('serviceName')}
              >
                Service <SortIcon columnKey="serviceName" />
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('currentMonth')}
              >
                Current Month <SortIcon columnKey="currentMonth" />
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('previousMonth')}
              >
                Previous Month <SortIcon columnKey="previousMonth" />
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('percentChange')}
              >
                Change <SortIcon columnKey="percentChange" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedData.map((item, index) => {
              const isIncrease = item.percentChange > 0;
              const isDecrease = item.percentChange < 0;
              const itemWithProvider = item as ServiceSpendWithProvider;
              const provider = itemWithProvider.provider;
              const ProviderIcon = provider ? providerIcons[provider] : null;
              const itemCurrency = showProvider && itemWithProvider.currency
                ? itemWithProvider.currency
                : currency;

              return (
                <tr
                  key={`${provider || 'default'}-${item.serviceId}-${index}`}
                  className="hover:bg-gray-50"
                >
                  {showProvider && (
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {provider && ProviderIcon && (
                        <span className="inline-flex items-center gap-1.5">
                          <ProviderIcon
                            className={`h-4 w-4 ${
                              provider === 'gcp'
                                ? 'text-blue-500'
                                : provider === 'twilio'
                                  ? 'text-green-500'
                                  : provider === 'openai'
                                    ? 'text-purple-500'
                                    : provider === 'deepgram'
                                      ? 'text-cyan-500'
                                      : 'text-orange-500'
                            }`}
                          />
                          {providerLabels[provider]}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {item.serviceName}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right font-mono">
                    {formatCurrency(item.currentMonth, itemCurrency)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 text-right font-mono">
                    {formatCurrency(item.previousMonth, itemCurrency)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span
                      className={`inline-flex items-center text-sm font-medium ${
                        isIncrease
                          ? 'text-red-600'
                          : isDecrease
                          ? 'text-green-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {isIncrease && <ArrowUp className="h-4 w-4 mr-1" />}
                      {isDecrease && <ArrowDown className="h-4 w-4 mr-1" />}
                      {!isIncrease && !isDecrease && <Minus className="h-4 w-4 mr-1" />}
                      {Math.abs(item.percentChange).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
